import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FeedbackPrompt } from "@/components/FeedbackPrompt";
import { Seo } from "@/components/Seo";
import { FileText, ArrowUpRight, Loader2, Newspaper, Sparkles } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

const isNewThisWeek = (d) => {
  if (!d) return false;
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= 7 * 24 * 60 * 60 * 1000 && t <= Date.now();
};

// Preferred display order + copy for named series
const SERIES_META = {
  "The Small Service Department": {
    order: 1,
    blurb: "A step-by-step series on building a real operating system for a small dealership service department — one process at a time.",
  },
  "Form Spotlights": {
    order: 2,
    blurb: "Short, focused looks at the individual forms behind the system — and how each one prevents a specific problem.",
  },
};

function ArticleCard({ a }) {
  const fresh = isNewThisWeek(a.date);
  return (
    <Link to={`/blog/${a.slug}`} data-testid={`resource-card-${a.slug}`}
      className="group border border-border bg-white flex flex-col btn-lift overflow-hidden">
      <div className="relative aspect-[3/2] overflow-hidden bg-secondary">
        {a.cover_image ? (
          <img src={a.cover_image} alt={a.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-primary/5 flex items-center justify-center">
            <FileText className="w-8 h-8 text-primary/40" />
          </div>
        )}
        {fresh && (
          <span data-testid={`resource-new-badge-${a.slug}`}
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-primary text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5">
            <Sparkles className="w-3 h-3" /> New this week
          </span>
        )}
      </div>
      <div className="p-7 flex flex-col flex-1">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          {a.category || "Article"}{a.date ? ` · ${fmtDate(a.date)}` : ""}
        </span>
        <h3 className="font-heading font-bold text-lg mt-2 leading-snug">{a.title}</h3>
        <p className="text-sm text-muted-foreground mt-3 flex-1 line-clamp-3">{a.excerpt}</p>
        <span className="mt-5 text-sm font-semibold text-primary inline-flex items-center gap-1">
          Read article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function Resources() {
  const [departments, setDepartments] = useState([]);
  const [systems, setSystems] = useState([]);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/systems").then((r) => setSystems(r.data)).catch(() => {});
    api.get("/blog").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  // Group by series
  const groups = {};
  const ungrouped = [];
  (articles || []).forEach((a) => {
    const s = (a.series || "").trim();
    if (s) { (groups[s] = groups[s] || []).push(a); }
    else { ungrouped.push(a); }
  });
  const orderedSeries = Object.keys(groups).sort(
    (x, y) => (SERIES_META[x]?.order || 99) - (SERIES_META[y]?.order || 99)
  );

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20" data-testid="resources-page">
      <Seo
        title="Resources & Articles | Centennial Business Systems"
        description="Practical operating articles on running dealership service, parts, sales, HR and collision departments — built on Process → Procedure → Reference — plus free forms and SOP resources."
        canonicalPath="/resources"
      />
      <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Resources</span>
      <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter mt-3 max-w-2xl">
        Insights from the floor, not the boardroom.
      </h1>
      <p className="text-muted-foreground mt-5 text-lg max-w-xl">
        Practical guidance on dealership operations, compliance, and building systems that last.
      </p>

      {articles === null ? (
        <div className="flex items-center gap-2 text-muted-foreground mt-14"><Loader2 className="w-5 h-5 animate-spin" /> Loading articles…</div>
      ) : articles.length === 0 ? (
        <div className="mt-14 border border-dashed border-border p-10 text-center" data-testid="resources-empty">
          <Newspaper className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground mt-3">New articles are on the way. Check back soon.</p>
          <Link to="/forms" className="inline-flex items-center gap-2 text-primary font-semibold mt-4">
            Browse the form library <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-16 space-y-20">
          {orderedSeries.map((s) => (
            <section key={s} data-testid={`series-section-${s.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-end justify-between gap-4 border-b border-border pb-4">
                <div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">Series</span>
                  <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight mt-1">{s}</h2>
                  {SERIES_META[s]?.blurb && (
                    <p className="text-muted-foreground text-sm mt-2 max-w-2xl">{SERIES_META[s].blurb}</p>
                  )}
                </div>
                <span className="text-sm font-mono text-muted-foreground whitespace-nowrap hidden sm:block">
                  {groups[s].length} {groups[s].length === 1 ? "article" : "articles"}
                </span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {groups[s].map((a) => <ArticleCard key={a.slug} a={a} />)}
              </div>
            </section>
          ))}

          {ungrouped.length > 0 && (
            <section data-testid="series-section-more">
              <div className="border-b border-border pb-4">
                <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight">More articles</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {ungrouped.map((a) => <ArticleCard key={a.slug} a={a} />)}
              </div>
            </section>
          )}
        </div>
      )}

      <div className="mt-20 bg-secondary border border-border p-10">
        <h2 className="font-heading font-bold text-2xl">Browse the library</h2>
        <p className="text-sm text-muted-foreground mt-1">The same {departments.reduce((a, d) => a + (d.count || 0), 0)} forms behind these articles — by department and by system.</p>

        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mt-6">By department</div>
        <div className="flex flex-wrap gap-3 mt-3">
          {departments.map((d) => (
            <Link key={d.name} to="/forms" data-testid={`resource-dept-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="bg-white border border-border px-4 py-2 text-sm hover:border-primary transition-colors">
              {d.name} <span className="text-muted-foreground font-mono">· {d.count}</span>
            </Link>
          ))}
        </div>

        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mt-6">By system</div>
        <div className="flex flex-wrap gap-3 mt-3">
          {systems.map((s) => (
            <Link key={s.code} to="/forms" data-testid={`resource-system-${s.code}`}
              className="bg-white border border-border px-4 py-2 text-sm hover:border-primary transition-colors">
              {s.code} · {s.short} <span className="text-muted-foreground font-mono">· {s.count}</span>
            </Link>
          ))}
        </div>

        <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3.5 font-semibold mt-8 btn-lift">
          Get full access <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <FeedbackPrompt />
    </div>
  );
}
